const getConstants = require('../../../utils/getConstants');
const classifyRepeaterEntryMode = require('./classifyRepeaterEntryMode');
const isInactiveBySkippedRepeaterAncestor = require('./isInactiveBySkippedRepeaterAncestor');

const constants = getConstants();
const { answerStates, formItemTypes, repeaterCadenceTypes } = constants;

const completionStates = Object.freeze({
   COMPLETE: 'complete',
   PARTIAL: 'partial',
   INCOMPLETE: 'incomplete',
   SKIPPED: 'skipped',
   EXCLUDED: 'excluded'
});

const normalizeId = (id) => (id == null ? null : id.toString().toLowerCase());
const hasState = (item, state) => !!item && (item.state & state) === state;
const isSkipped = (item) => hasState(item, answerStates.SKIPPED);
const isAnswered = (item) => hasState(item, answerStates.ANSWERED);
const isCurrentlyValid = (item) => !hasState(item, answerStates.INVALID);

function buildIndex(items) {
   const list = Array.isArray(items) ? items : [];
   const childrenByParent = new Map();
   for (const item of list) {
      const parentId = normalizeId(item.idParent);
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(item);
   }
   return { items: list, childrenByParent };
}

function getChildren(index, item) {
   return index.childrenByParent.get(normalizeId(item && item.id)) || [];
}

function isContainer(item) {
   return item && [formItemTypes.SECTION.id, formItemTypes.REPEATER_GROUP.id, formItemTypes.REPEATER.id].includes(item.idItemType);
}

function getRepeaterStructuralState(item, groupCount) {
   const options = item?.options || {};
   const classification = classifyRepeaterEntryMode({
      idRepeaterType: options.idRepeaterType,
      entryMode: options.entryMode,
      idRepeaterCadence: options.idRepeaterCadence,
      timesToRepeat: options.timesToRepeat
   });

   if (classification.kind === classifyRepeaterEntryMode.kinds.CANONICAL_FULL_LIST) {
      return {
         satisfied: item?.repeaterMaterialized === true,
         requiredAdditionalEntries: 0,
         classificationKind: classification.kind,
         materialized: item?.repeaterMaterialized === true
      };
   }

   const required = Number(options.timesToRepeat) || 0;
   let satisfied = true;
   let requiredAdditionalEntries = 0;

   switch (options.idRepeaterCadence) {
      case repeaterCadenceTypes.EXACTLY.id:
         satisfied = groupCount === required;
         requiredAdditionalEntries = Math.max(0, required - groupCount);
         break;
      case repeaterCadenceTypes.AT_LEAST.id:
         satisfied = groupCount >= required;
         requiredAdditionalEntries = Math.max(0, required - groupCount);
         break;
      case repeaterCadenceTypes.AT_MOST.id:
         satisfied = groupCount <= required;
         break;
      case repeaterCadenceTypes.ANY_NUMBER_OF.id:
         satisfied = groupCount > 0;
         requiredAdditionalEntries = groupCount > 0 ? 0 : 1;
         break;
      default:
         if (classification.kind === classifyRepeaterEntryMode.kinds.LEGACY_FOREACH) {
            const listCount = Array.isArray(options.listOptions) ? options.listOptions.length : 0;
            satisfied = groupCount === listCount;
            requiredAdditionalEntries = Math.max(0, listCount - groupCount);
         }
         break;
   }

   return {
      satisfied,
      requiredAdditionalEntries,
      classificationKind: classification.kind,
      materialized: null
   };
}

function deriveItemCompletionInternal(item, index, memo) {
   if (!item) {
      return { applicable: false, state: completionStates.EXCLUDED, complete: false, partial: false, skipped: false, hasProgress: false };
   }

   const key = normalizeId(item.id);
   if (memo.has(key)) return memo.get(key);

   if (isInactiveBySkippedRepeaterAncestor(index.items, item.id)) {
      const excluded = {
         applicable: false,
         state: completionStates.EXCLUDED,
         complete: false,
         partial: false,
         skipped: false,
         hasProgress: false
      };
      memo.set(key, excluded);
      return excluded;
   }

   if (isSkipped(item)) {
      const skipped = {
         applicable: true,
         state: completionStates.SKIPPED,
         complete: true,
         partial: false,
         skipped: true,
         hasProgress: true
      };
      memo.set(key, skipped);
      return skipped;
   }

   if (!isContainer(item)) {
      const answered = isAnswered(item);
      const valid = isCurrentlyValid(item);
      const complete = answered && valid;
      const leaf = {
         applicable: true,
         state: complete ? completionStates.COMPLETE : completionStates.INCOMPLETE,
         complete,
         partial: false,
         skipped: false,
         answered,
         valid,
         hasProgress: answered
      };
      memo.set(key, leaf);
      return leaf;
   }

   const childResults = getChildren(index, item)
      .map((child) => deriveItemCompletionInternal(child, index, memo))
      .filter((result) => result.applicable);

   if (item.idItemType === formItemTypes.REPEATER.id) {
      const groupItems = getChildren(index, item).filter((child) => child.idItemType === formItemTypes.REPEATER_GROUP.id);
      const groups = groupItems.map((group) => deriveItemCompletionInternal(group, index, memo)).filter((result) => result.applicable);
      const completed = groups.filter((result) => result.complete).length;
      const total = groups.length;
      const allRowsComplete = groups.every((result) => result.complete);
      const hasIncompleteRows = groups.some((result) => !result.complete);
      const hasRowProgress = groups.some((result) => result.hasProgress || result.complete);
      const structural = getRepeaterStructuralState(item, total);
      const complete = allRowsComplete && structural.satisfied;
      const partial = !complete && hasIncompleteRows && hasRowProgress;
      const result = {
         applicable: true,
         state: complete ? completionStates.COMPLETE : partial ? completionStates.PARTIAL : completionStates.INCOMPLETE,
         complete,
         partial,
         skipped: false,
         hasProgress: hasRowProgress,
         progress: { completed, total },
         structural
      };
      memo.set(key, result);
      return result;
   }

   const complete = childResults.every((result) => result.complete);
   const hasProgress = childResults.some((result) => result.hasProgress || result.complete);
   const partial = !complete && hasProgress;
   const result = {
      applicable: true,
      state: complete ? completionStates.COMPLETE : partial ? completionStates.PARTIAL : completionStates.INCOMPLETE,
      complete,
      partial,
      skipped: false,
      hasProgress
   };
   memo.set(key, result);
   return result;
}

function deriveFormItemCompletion({ item, items }) {
   const index = buildIndex(items);
   return deriveItemCompletionInternal(item, index, new Map());
}

function deriveRepeaterProgress({ item, items }) {
   const result = deriveFormItemCompletion({ item, items });
   return {
      state: result.state,
      complete: result.complete,
      skipped: result.skipped,
      partial: result.partial,
      progress: result.progress || { completed: 0, total: 0 },
      structural: result.structural || { satisfied: true, requiredAdditionalEntries: 0 }
   };
}

deriveFormItemCompletion.states = completionStates;
deriveRepeaterProgress.states = completionStates;

module.exports = {
   completionStates,
   deriveFormItemCompletion,
   deriveRepeaterProgress,
   getRepeaterStructuralState
};
